import { adjustLuxLoyaltyPointsEarn, calculateLuxLoyaltyStatusCreditsFromPoints } from '@luxuryescapes/lib-lux-loyalty'

export interface AdjustedEarnTotals {
  earnableBasePoints: number
  earnableBonusPoints: number
  earnableStatusCredits: number
  earnableBonusStatusCredits: number
  earnablePoints: number
}

interface Params {
  defaultPayableItemPrice: number
  adjustedItemPrice: number
  basePoints: number
  bonusPoints: number
  statusCredits: number
  bonusStatusCredits: number
  roundingEnabled: boolean
  earnCap?: App.Checkout.LoyaltyEarnCap
}

export default function calculateAdjustedEarnTotals({
  defaultPayableItemPrice,
  adjustedItemPrice,
  basePoints,
  bonusPoints,
  statusCredits,
  bonusStatusCredits,
  roundingEnabled,
  earnCap,
}: Params): AdjustedEarnTotals {
  if (!defaultPayableItemPrice) {
    return {
      earnableBasePoints: basePoints,
      earnableBonusPoints: bonusPoints,
      earnableStatusCredits: statusCredits,
      earnableBonusStatusCredits: bonusStatusCredits,
      earnablePoints: basePoints + bonusPoints,
    }
  }

  const itemPointsEarnRatio = basePoints / defaultPayableItemPrice

  let earnableBasePoints = basePoints
  let earnableStatusCredits = statusCredits

  if (defaultPayableItemPrice !== adjustedItemPrice) {
    if (roundingEnabled) {
      earnableBasePoints = Math.floor(adjustLuxLoyaltyPointsEarn(adjustedItemPrice * itemPointsEarnRatio))
    } else {
      earnableBasePoints = Math.floor(adjustedItemPrice * itemPointsEarnRatio)
    }

    if (statusCredits > 0) {
      earnableStatusCredits = calculateLuxLoyaltyStatusCreditsFromPoints(earnableBasePoints)
    }
  }

  if (earnCap) {
    earnableBasePoints = Math.min(earnableBasePoints, earnCap.points)
    earnableStatusCredits = Math.min(earnableStatusCredits, earnCap.statusCredits)
  }

  let earnableBonusPoints = bonusPoints
  let earnableBonusStatusCredits = bonusStatusCredits

  if (defaultPayableItemPrice !== adjustedItemPrice) {
    if (basePoints > 0 && (bonusPoints % basePoints === 0)) {
      const bonusPointsMultiplier = bonusPoints / basePoints
      earnableBonusPoints = earnableBasePoints * bonusPointsMultiplier
    }

    if (statusCredits > 0 && (bonusStatusCredits % statusCredits === 0)) {
      const bonusStatusCreditsMultiplier = bonusStatusCredits / statusCredits
      earnableBonusStatusCredits = earnableStatusCredits * bonusStatusCreditsMultiplier
    }
  }

  return {
    earnableBasePoints,
    earnableBonusPoints,
    earnableStatusCredits,
    earnableBonusStatusCredits,
    earnablePoints: earnableBasePoints + earnableBonusPoints,
  }
}
